import { useState } from "react";
import { CheckCircle2, CreditCard, Loader2, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const METHODS = ["GCash", "Maya", "QR Ph", "Cards"];

const TIERS = [
  {
    id: "tier_monthly",
    name: "Monthly",
    label: "₱499 / mo",
    subtitle: "Cancel anytime",
  },
  {
    id: "tier_6mo",
    name: "6-month",
    label: "₱2,394",
    subtitle: "20% off",
  },
  {
    id: "tier_annual",
    name: "Annual",
    label: "₱3,588",
    subtitle: "40% off · best value",
  },
];

/**
 * Shown when a broker's trial has ended and they hit a 402 UPGRADE_REQUIRED
 * (or when the pro-nudge banners are triggered). Broker picks a tier and
 * "Upgrade now" starts a PayMongo checkout.
 */
export function UpgradeModal({ open, onOpenChange }) {
  const [starting, setStarting] = useState(false);
  const [selectedTier, setSelectedTier] = useState("tier_monthly");
  const navigate = useNavigate();

  const startCheckout = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        platform: "web",
        tier_id: selectedTier,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e, "Failed to start checkout"));
      setStarting(false);
    }
  };

  const goBilling = () => {
    onOpenChange(false);
    navigate("/dashboard/billing");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md rounded-2xl border-gray-200 bg-white p-8 sm:p-10"
        data-testid="upgrade-modal"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#35622F]/10">
          <Sparkles className="h-6 w-6 text-[#35622F]" />
        </div>
        <DialogTitle
          className="mt-6 text-center text-2xl font-bold tracking-tight text-gray-900"
          data-testid="upgrade-modal-title"
        >
          Upgrade MatchBroker
        </DialogTitle>
        <DialogDescription
          className="mt-3 text-center text-sm leading-relaxed text-gray-600"
          data-testid="upgrade-modal-value-prop"
        >
          Your free trial covered your first 30 days or 3 matches. Choose a
          paid tier to keep listing, matching, and closing.
        </DialogDescription>

        <div className="mt-6 space-y-2">
          {TIERS.map((t) => {
            const isSelected = selectedTier === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelectedTier(t.id)}
                className={`mb-focus-ring flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition ${
                  isSelected
                    ? "border-brand-green bg-brand-green/5"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
                data-testid={`upgrade-modal-tier-${t.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    {t.name}
                  </div>
                  <div className="mt-0.5 text-[11px] text-gray-500">
                    {t.subtitle}
                  </div>
                </div>
                <div className="text-sm font-bold text-gray-900">{t.label}</div>
              </button>
            );
          })}
        </div>

        <ul className="mt-6 space-y-2 text-sm text-gray-700">
          {[
            "Unlimited Active listings",
            "Priority in the AI matching queue",
            "Full co-broker score analytics",
          ].map((f) => (
            <li key={f} className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-[#35622F]" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <div
          className="mt-6 flex flex-wrap justify-center gap-2"
          data-testid="upgrade-modal-methods"
        >
          {METHODS.map((m) => (
            <span
              key={m}
              className="inline-flex items-center rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-700"
              data-testid={`upgrade-modal-method-${m.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {m}
            </span>
          ))}
        </div>

        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={starting}
            className="mb-focus-ring h-11 rounded-xl border-gray-200 bg-white px-5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            data-testid="upgrade-modal-later"
          >
            Maybe later
          </Button>
          <Button
            type="button"
            onClick={startCheckout}
            disabled={starting}
            className="mb-focus-ring h-11 rounded-xl bg-[#1B2B4B] px-6 text-sm font-semibold text-white hover:bg-[#14213A] disabled:opacity-60"
            data-testid="upgrade-modal-cta-primary"
          >
            {starting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting checkout…
              </>
            ) : (
              <>
                <CreditCard className="mr-2 h-4 w-4" />
                Upgrade now
              </>
            )}
          </Button>
        </div>

        <div className="mt-5 text-center">
          <button
            type="button"
            onClick={goBilling}
            className="text-xs font-medium text-[#1B2B4B] hover:underline"
            data-testid="upgrade-modal-view-billing"
          >
            View billing details
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
